import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { authService } from '../services/api';
import { ArrowLeft, Mail, KeyRound, Shield, Loader2, CheckCircle2 } from 'lucide-react';

export default function ForgotPassword() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password, 4: Success
    const [isLoading, setIsLoading] = useState(false);
    
    // Form state
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const handleSendOTP = async (e) => {
        e.preventDefault();
        if (!email) {
            toast.error('Please enter your email');
            return;
        }
        
        setIsLoading(true);
        try {
            await axios.post(`${API}/auth/forgot-password`, { email });
            toast.success('OTP sent to your email');
            setStep(2);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send OTP');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        if (otp.length !== 6) {
            toast.error('Please enter a valid 6-digit OTP');
            return;
        }
        
        setIsLoading(true);
        try {
            await axios.post(`${API}/auth/verify-otp`, { email, otp });
            toast.success('OTP verified successfully');
            setStep(3);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid OTP');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        
        if (newPassword.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        
        setIsLoading(true);
        try {
            await axios.post(`${API}/auth/reset-password`, { 
                email, 
                otp, 
                new_password: newPassword 
            });
            toast.success('Password reset successfully');
            setStep(4);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reset password');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOTPChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setOtp(value);
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                        {step === 4 ? (
                            <CheckCircle2 className="w-8 h-8 text-green-500" />
                        ) : step === 3 ? (
                            <KeyRound className="w-8 h-8 text-primary" />
                        ) : step === 2 ? (
                            <Shield className="w-8 h-8 text-primary" />
                        ) : (
                            <Mail className="w-8 h-8 text-primary" />
                        )}
                    </div>
                    <CardTitle className="text-2xl">
                        {step === 4 ? 'Password Reset!' : 
                         step === 3 ? 'Set New Password' : 
                         step === 2 ? 'Verify OTP' : 
                         'Forgot Password'}
                    </CardTitle>
                    <CardDescription>
                        {step === 4 ? 'Your password has been successfully reset' :
                         step === 3 ? 'Create a new secure password' :
                         step === 2 ? 'Enter the 6-digit code sent to your email' :
                         'Enter your email to receive a reset code'}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {/* Step 1: Email Input */}
                    {step === 1 && (
                        <form onSubmit={handleSendOTP} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">Email Address</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="name@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="forgot-email-input"
                                />
                            </div>
                            <Button 
                                type="submit" 
                                className="w-full" 
                                disabled={isLoading}
                                data-testid="send-otp-btn"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Sending OTP...
                                    </>
                                ) : (
                                    'Send OTP'
                                )}
                            </Button>
                        </form>
                    )}

                    {/* Step 2: OTP Verification */}
                    {step === 2 && (
                        <form onSubmit={handleVerifyOTP} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="otp">Enter OTP</Label>
                                <Input
                                    id="otp"
                                    type="text"
                                    placeholder="Enter 6-digit OTP"
                                    value={otp}
                                    onChange={handleOTPChange}
                                    maxLength={6}
                                    className="text-center text-2xl tracking-[0.5em] font-mono"
                                    required
                                    data-testid="otp-input"
                                />
                                <p className="text-xs text-muted-foreground text-center">
                                    OTP sent to {email}
                                </p>
                            </div>
                            <Button 
                                type="submit" 
                                className="w-full" 
                                disabled={isLoading || otp.length !== 6}
                                data-testid="verify-otp-btn"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Verifying...
                                    </>
                                ) : (
                                    'Verify OTP'
                                )}
                            </Button>
                            <Button 
                                type="button" 
                                variant="ghost" 
                                className="w-full"
                                onClick={() => {
                                    setOtp('');
                                    handleSendOTP({ preventDefault: () => {} });
                                }}
                                disabled={isLoading}
                            >
                                Resend OTP
                            </Button>
                        </form>
                    )}

                    {/* Step 3: New Password */}
                    {step === 3 && (
                        <form onSubmit={handleResetPassword} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="new-password">New Password</Label>
                                <Input
                                    id="new-password"
                                    type="password"
                                    placeholder="Enter new password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    minLength={6}
                                    required
                                    data-testid="new-password-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirm-password">Confirm Password</Label>
                                <Input
                                    id="confirm-password"
                                    type="password"
                                    placeholder="Confirm new password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    minLength={6}
                                    required
                                    data-testid="confirm-password-input"
                                />
                            </div>
                            <Button 
                                type="submit" 
                                className="w-full" 
                                disabled={isLoading}
                                data-testid="reset-password-btn"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Resetting Password...
                                    </>
                                ) : (
                                    'Reset Password'
                                )}
                            </Button>
                        </form>
                    )}

                    {/* Step 4: Success */}
                    {step === 4 && (
                        <div className="text-center space-y-4">
                            <p className="text-muted-foreground">
                                You can now login with your new password.
                            </p>
                            <Button 
                                className="w-full"
                                onClick={() => navigate('/login')}
                                data-testid="go-to-login-btn"
                            >
                                Go to Login
                            </Button>
                        </div>
                    )}

                    {/* Back to Login link */}
                    {step !== 4 && (
                        <div className="mt-6 text-center">
                            <Button
                                variant="link"
                                className="text-sm"
                                onClick={() => navigate('/login')}
                                data-testid="back-to-login-btn"
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back to Login
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
